/* Humbler reviews: sections/humbler-product-reviews.liquid and sections/humbler-home-reviews.liquid ([data-hb-reviews]).
   Styles: assets/humbler-reviews.css.
   1. Tabs "This piece" / "All reviews": WAI-ARIA tabs (click, Left/Right/Home/End, automatic activation). The hidden
      panel stays laid out at full width (CSS), so Judge.me keeps measuring its widgets and switching is instant.
      Nothing is remembered between pages.
   2. All reviews: the published store reviews, read from Judge.me's public all-reviews feed (cdn.judge.me,
      read-only, cached by the browser for 20 minutes) and drawn into the section's own row template, 5 at a time
      with "Show more reviews". No sort control: highest rated first, reviews with photos first within a rating.
      The feed has no such order, so it is read one star rating at a time (filter_rating 5 down to 1, each
      "pictures first"); a page that ends a rating carries on into the next. The feed's HTML is parsed inert
      (<template>) and only its text, the product link (/products/…) and https photo URLs are used. The first page
      is fetched as the section nears the viewport, so the tab never waits. The feed stops at 1,000 reviews per
      rating (later pages repeat the last one): the list skips the rest of that rating, says so at the end, and
      never shows a review twice.
   3. Photo lightbox for those rows, the sourced rows and the photos in Judge.me's own rows, via window.HumblerUI
      dialogs when present.
   4. Judge.me helpers:
      - Judge.me loads its write-a-review script only after the widget has sat in view through real scroll events,
        so its "Write a review" button used to pop in seconds late. It is requested as the section nears the
        viewport instead (the same three calls Judge.me's own click handler makes), into a slot that already has
        the button's height.
      - "Write a review" in the All reviews tab (a product with no reviews yet) switches to "This piece" and opens
        Judge.me's own form there.
      - Featured carousel photos (data-src) load as the carousel nears the viewport, not after the first scroll;
        a card's "read review" link that takes keyboard focus slides its card into view (Judge.me's own slider).
      - "This piece": its sort select is hidden (CSS) and each page Judge.me draws is put in the All reviews order
        (highest rated first, photos first within a rating), its empty state gets our words, and its loading
        skeleton is fitted to the rows Judge.me is about to draw (title, body length, photo), so the swap barely
        moves the page.
   Never submits anything: Judge.me's form is only opened, by the shopper's click. */
(() => {
  if (window.HumblerReviews) return;

  const FEED = 'https://cdn.judge.me/reviews/all_reviews_js_based';
  const RATINGS = [5, 4, 3, 2, 1]; // All reviews is read one star rating at a time, photos first within each
  const FEED_CAP = 1000; // the feed returns nothing past an offset of 1,000 per query: every later page repeats the last one
  const fill = (text, values) => String(text || '').replace(/%(\w+)%/g, (match, key) => (key in values ? values[key] : match));
  const lang = document.documentElement.lang || undefined;
  const number = value => { try { return Number(value).toLocaleString(lang); } catch { return String(value); } };
  const root = () => ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/?$/, '/');
  const near = (element, margin, callback) => {
    if (!('IntersectionObserver' in window)) { callback(); return; }
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      observer.disconnect();
      callback();
    }, { rootMargin: `${margin}px 0px` });
    observer.observe(element);
  };

  /* ---------- 1. tabs ---------- */
  function initTabs(section) {
    const list = section.querySelector('[data-hb-reviews-tabs]');
    if (!list) return { select() {} };
    const tabs = [...list.querySelectorAll('[role="tab"]')];
    const select = (tab, focus) => {
      if (!tab) return;
      for (const item of tabs) {
        const on = item === tab;
        item.setAttribute('aria-selected', on ? 'true' : 'false');
        item.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(item.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      }
      if (focus) tab.focus();
      section.dispatchEvent(new CustomEvent('hb:reviews-tab', { detail: { name: tab.dataset.hbReviewsTab } }));
    };
    list.addEventListener('click', event => {
      const tab = event.target.closest('[role="tab"]');
      if (tab && tab.getAttribute('aria-selected') !== 'true') select(tab, false);
    });
    list.addEventListener('keydown', event => {
      const index = tabs.indexOf(document.activeElement);
      if (index < 0) return;
      const next = { ArrowRight: tabs[(index + 1) % tabs.length], ArrowLeft: tabs[(index - 1 + tabs.length) % tabs.length], Home: tabs[0], End: tabs[tabs.length - 1] }[event.key];
      if (!next) return;
      event.preventDefault();
      select(next, true);
    });
    return { select: (name, focus = false) => select(tabs.find(tab => tab.dataset.hbReviewsTab === name), focus) };
  }

  /* ---------- 2. All reviews feed ---------- */
  const safeUrl = value => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.href);
      return url.protocol === 'https:' ? url.href : '';
    } catch { return ''; }
  };
  const clean = text => String(text || '').replace(/[ \t\f\v ]+/g, ' ').replace(/ *\n */g, '\n').trim();
  function parse(html) {
    const template = document.createElement('template');
    template.innerHTML = html; // inert: nothing runs, nothing loads
    return [...template.content.querySelectorAll('.jdgm-rev')].map(node => {
      const text = selector => clean(node.querySelector(selector)?.textContent);
      const href = node.querySelector('a.jdgm-rev__prod-link')?.getAttribute('href') || '';
      const handle = (href.match(/^\/products\/([^/?#]+)/) || [])[1] || '';
      const body = [...node.querySelectorAll('.jdgm-rev__body p')].map(p => {
        p.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
        return clean(p.textContent);
      }).filter(Boolean);
      if (!body.length) { const loose = text('.jdgm-rev__body'); if (loose) body.push(loose); }
      const photos = [...node.querySelectorAll('.jdgm-rev__pic-link')].map(link => {
        const img = link.querySelector('img');
        return { full: safeUrl(link.getAttribute('href') || link.getAttribute('data-mfp-src')), thumb: safeUrl(img?.getAttribute('data-src') || img?.getAttribute('src')) };
      }).filter(photo => photo.full || photo.thumb);
      return {
        id: node.getAttribute('data-review-id') || '',
        verified: node.getAttribute('data-verified-buyer') === 'true',
        score: Math.max(0, Math.min(5, Number(node.querySelector('.jdgm-rev__rating')?.getAttribute('data-score')) || 0)),
        author: text('.jdgm-rev__author'),
        title: text('.jdgm-rev__title'),
        body,
        product: { title: text('.jdgm-rev__prod-link'), handle },
        photos,
        reply: text('.jdgm-rev__reply-content') || text('.jdgm-rev__reply')
      };
    });
  }

  function row(template, review, strings) {
    const item = template.content.firstElementChild.cloneNode(true);
    const stars = item.querySelector('.hb-review__stars');
    stars.style.setProperty('--_rating', review.score);
    stars.setAttribute('aria-label', fill(strings.stars, { rating: review.score }));
    item.querySelector('.hb-review__author').textContent = review.author;
    if (!review.verified) item.querySelector('.hb-review__badge')?.remove();
    const product = item.querySelector('.hb-review__product');
    if (review.product.handle && review.product.title) {
      const link = product.querySelector('a');
      link.href = `${root()}products/${review.product.handle}`;
      link.textContent = review.product.title;
    } else product.remove();
    const title = item.querySelector('.hb-review__title');
    if (review.title) title.textContent = review.title; else title.remove();
    const body = item.querySelector('.hb-review__body');
    for (const text of review.body) { const p = document.createElement('p'); p.textContent = text; body.append(p); }
    if (!review.body.length) body.remove();
    const photos = item.querySelector('.hb-review__photos');
    review.photos.forEach((photo, index) => {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hb-review__photo';
      button.dataset.hbReviewPhoto = photo.full || photo.thumb;
      button.dataset.hbReviewName = review.author;
      if (review.product.title) button.dataset.hbReviewProduct = review.product.title;
      button.setAttribute('aria-label', fill(strings.photo, { index: index + 1, total: review.photos.length, name: review.author }));
      const img = document.createElement('img');
      img.alt = '';
      img.width = 80;
      img.height = 80;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.src = photo.thumb || photo.full;
      button.append(img);
      li.append(button);
      photos.append(li);
    });
    const reply = item.querySelector('.hb-review__reply');
    if (review.reply) reply.querySelector('.hb-review__reply-text').textContent = review.reply; else reply.remove();
    return item;
  }

  function initFeed(section, feed, tabs, strings) {
    const list = feed.querySelector('[data-hb-reviews-list]');
    const template = feed.querySelector('template[data-hb-review-template]');
    const shop = feed.dataset.shop || window.Shopify?.shop || '';
    if (!list || !template) return;
    const more = feed.querySelector('[data-hb-reviews-more]');
    const progress = feed.querySelector('[data-hb-reviews-progress]');
    const cap = feed.querySelector('[data-hb-reviews-cap]');
    const status = feed.querySelector('[data-hb-reviews-status]');
    const perPage = Number(feed.dataset.perPage) || 5;
    let total = Number(feed.dataset.total) || 0; // the store's count (section setting); each query only knows its rating's
    let tier = 0; // index in RATINGS of the rating being read
    let tierPage = 0; // pages read of that rating
    let shown = 0;
    let capped = false; // a rating had more reviews than the feed lists: the rest of it was skipped
    let seen = new Set();
    let started = false;
    let controller = null;
    let failed = null; // the failed request: the first page (true) or the next page (false)

    const url = (rating, page) => {
      const params = new URLSearchParams({ url: shop, shop_domain: shop, platform: 'shopify', page: String(page), per_page: String(perPage), review_type: 'product-reviews', sort_by: 'pictures_first', sort_dir: 'desc', filter_rating: String(rating) });
      return `${FEED}?${params}`;
    };
    const finished = () => tier >= RATINGS.length;
    // Announcements ("Showing 10 of 1,151") are for screen readers only; an error shows, with a retry button that
    // repeats the request that failed. Focus leaves the button before it goes: to "Show more reviews" after a
    // failed next page, to the All reviews tab after a failed first page.
    const setStatus = (message, retry) => {
      status.replaceChildren();
      status.classList.toggle('is-error', !!retry);
      if (!message) return;
      status.append(message);
      if (retry) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'hb-link hb-reviews__retry';
        button.textContent = strings.retry || 'Try again';
        button.addEventListener('click', () => {
          const reset = failed !== false;
          const target = !reset && more && !more.hidden ? more : section.querySelector('[data-hb-reviews-tab="all"]');
          if (document.activeElement === button) target?.focus({ preventScroll: true });
          load(reset);
        });
        status.append(' ', button);
      }
    };
    const render = () => {
      if (progress) {
        progress.hidden = shown === 0;
        progress.textContent = fill(strings.showing, { shown: number(shown), total: number(Math.max(total, shown)) });
      }
      if (cap) cap.hidden = !(capped && finished());
      if (more) {
        const done = shown === 0 || finished();
        // the button leaves with focus on it (end of the list): focus moves to the count it leaves behind
        if (done && document.activeElement === more && progress && !progress.hidden) { progress.tabIndex = -1; progress.focus({ preventScroll: true }); }
        more.hidden = done;
      }
    };

    async function load(reset) {
      controller?.abort();
      const own = new AbortController();
      controller = own;
      const first = reset && !list.querySelector('.hb-review:not([data-hb-skeleton])');
      if (reset && !first) feed.setAttribute('aria-busy', 'true'); // a reload: the rows dim in place (.hb-busy-dim)
      if (!reset) more?.classList.add('is-loading');
      if (reset) list.style.minHeight = `${list.offsetHeight}px`;
      // Read into copies, so a failed request leaves the list as it was and "Try again" repeats it.
      let at = reset ? 0 : tier;
      let pageIn = reset ? 0 : tierPage;
      let skipped = reset ? false : capped;
      const known = reset ? new Set() : new Set(seen);
      const fresh = [];
      try {
        // A page is perPage reviews: when a rating runs out part-way, it carries on into the next rating.
        while (fresh.length < perPage && at < RATINGS.length) {
          const response = await fetch(url(RATINGS[at], pageIn + 1), { signal: own.signal, credentials: 'omit' });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();
          const reviews = parse(String(data.html || ''));
          const size = Number(data.number_of_product_reviews); // with filter_rating: that rating's count
          pageIn += 1;
          // Never the same review twice (past the feed's limit it repeats its last page).
          const added = reviews.filter(review => !review.id || !known.has(review.id));
          added.forEach(review => { if (review.id) known.add(review.id); });
          fresh.push(...added);
          const read = pageIn * perPage;
          if (reviews.length < perPage || !added.length || read >= FEED_CAP || (Number.isFinite(size) && read >= size)) {
            if (read >= FEED_CAP && Number.isFinite(size) && size > FEED_CAP) skipped = true;
            at += 1;
            pageIn = 0;
          }
        }
        tier = at;
        tierPage = pageIn;
        capped = skipped;
        seen = known;
        const rows = document.createDocumentFragment();
        fresh.forEach(review => rows.append(row(template, review, strings)));
        if (reset) { list.replaceChildren(rows); shown = 0; } else list.append(rows);
        shown += fresh.length;
        failed = null;
        if (finished() && !capped) total = shown; // the real end of the list
        if (!shown) setStatus(strings.empty || '');
        else if (!reset) setStatus([fill(strings.showing, { shown: number(shown), total: number(Math.max(total, shown)) }), capped && finished() ? clean(cap?.textContent) : ''].filter(Boolean).join(' '));
        else setStatus('');
        render();
        window.HumblerSkeleton?.markLoaded?.(list);
      } catch (error) {
        if (own.signal.aborted) return;
        failed = reset;
        list.querySelectorAll('[data-hb-skeleton]').forEach(node => node.remove());
        setStatus(strings.error || '', true);
        console.warn('[humbler-reviews] All reviews could not load', error);
      } finally {
        if (controller === own) {
          controller = null;
          feed.removeAttribute('aria-busy');
          more?.classList.remove('is-loading');
          list.style.minHeight = '';
        }
      }
    }
    const start = () => { if (started) return; started = true; load(true); };

    more?.addEventListener('click', () => { if (!controller && !finished()) load(false); });
    section.addEventListener('hb:reviews-tab', event => { if (event.detail?.name === 'all') start(); });
    if (!feed.closest('[role="tabpanel"][hidden]')) near(feed, 800, start); // the visible panel: as it nears the viewport
    else near(section, 800, start); // the hidden one: prefetched with the section, so the tab switch is instant
  }

  /* ---------- 3. photo lightbox ---------- */
  function initLightbox(section) {
    const dialog = section.querySelector('[data-hb-reviews-lightbox]');
    if (!dialog) return;
    const image = dialog.querySelector('[data-hb-reviews-lightbox-image]');
    const caption = dialog.querySelector('[data-hb-reviews-lightbox-caption]');
    const steps = [...dialog.querySelectorAll('[data-hb-reviews-lightbox-step]')];
    let photos = []; // { src, name, product }
    let index = 0;
    const show = next => {
      index = (next + photos.length) % photos.length;
      const photo = photos[index];
      image.src = photo.src;
      image.alt = fill(dialog.dataset.alt, { name: photo.name || '' });
      caption.textContent = [photo.name, photo.product].filter(Boolean).join(' · ');
      steps.forEach(step => { step.hidden = photos.length < 2; });
    };
    const open = (list, start, opener) => {
      photos = list;
      show(Math.max(0, start));
      if (window.HumblerUI?.openDialog) window.HumblerUI.openDialog(dialog, opener);
      else if (!dialog.open) dialog.showModal();
    };
    // our rows: buttons carrying the photo
    section.addEventListener('click', event => {
      const button = event.target.closest('[data-hb-review-photo]');
      if (!button || !section.contains(button)) return;
      event.preventDefault();
      const buttons = [...(button.closest('.hb-review')?.querySelectorAll('[data-hb-review-photo]') || [button])];
      open(buttons.map(item => ({ src: item.dataset.hbReviewPhoto, name: item.dataset.hbReviewName, product: item.dataset.hbReviewProduct })), buttons.indexOf(button), button);
    });
    // Judge.me's rows ("This piece"): caught before Judge.me's own popup (capture phase on the section), so its
    // photos open here too. A modified click (new tab) keeps the link's own behaviour.
    section.addEventListener('click', event => {
      const link = event.target.closest?.('.jdgm-widget .jdgm-rev__pic-link');
      if (!link || !section.contains(link) || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const review = link.closest('.jdgm-rev');
      const links = [...(review || link.parentElement).querySelectorAll('.jdgm-rev__pic-link')];
      const name = clean(review?.querySelector('.jdgm-rev__author')?.textContent);
      const list = links.map(item => ({ src: safeUrl(item.getAttribute('href') || item.getAttribute('data-mfp-src')), name, product: '' }));
      const start = links.indexOf(link);
      if (!list[start]?.src) return;
      event.preventDefault();
      event.stopPropagation();
      open(list.filter(photo => photo.src), list.slice(0, start).filter(photo => photo.src).length, link);
    }, true);
    steps.forEach(step => step.addEventListener('click', () => show(index + Number(step.dataset.hbReviewsLightboxStep))));
    dialog.addEventListener('keydown', event => {
      if (photos.length < 2) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); show(index + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(index - 1); }
    });
    if (!window.HumblerUI) {
      dialog.addEventListener('click', event => {
        if (event.target === dialog || event.target.closest('[data-hb-close]')) dialog.close();
      });
    }
    dialog.addEventListener('close', () => { image.removeAttribute('src'); });
  }

  /* ---------- 4. Judge.me helpers ---------- */
  const formRequested = () => {
    const jdgm = window.jdgm;
    if (!jdgm) return false;
    if (typeof jdgm.openForm === 'function') return true;
    return (jdgm.loadScript?.requestedUrls || []).some(url => /form\.js/.test(String(url)));
  };
  function requestForm() {
    const jdgm = window.jdgm;
    if (!jdgm || formRequested() || typeof jdgm.loadScript !== 'function' || !jdgm.CDN_HOST) return;
    try {
      jdgm.loadScript(`${jdgm.CDN_HOST}widget/write_review_modal.js`);
      jdgm.loadScript(`${jdgm.CDN_HOST}widget/form.js`);
      if (typeof jdgm.loadCSS === 'function' && typeof jdgm.widgetPath === 'function') jdgm.loadCSS(jdgm.widgetPath('form.css'));
    } catch (error) { console.warn('[humbler-reviews] Judge.me form preload', error); }
  }
  const whenJudgeme = callback => {
    if (window.jdgm?._doneSetup || document.querySelector('.jdgm--done-setup-widget')) { callback(); return; }
    document.addEventListener('jdgm.doneSetup', () => callback(), { once: true });
  };

  // Judge.me's own jQuery: no slide animations (its pagination collapsed the list and slid it back open; the CSS
  // keeps the rows in place instead). Its scroll to the reviews (after a page or sort change, opening the form) goes
  // through Judge.me's documented jdgmSettings.scrollTo hook: for our sections it stops below the sticky header
  // (it scrolled the list under it) and jumps instead of gliding under reduced motion.
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  function tuneJudgeme() {
    const jdgm = window.jdgm;
    if (jdgm?.$?.fx) jdgm.$.fx.off = true;
    const settings = window.jdgmSettings;
    if (!settings || settings.hbScrollHook) return;
    const fallback = typeof settings.scrollTo === 'function' ? settings.scrollTo : null;
    settings.hbScrollHook = true;
    settings.scrollTo = (target, offset) => {
      const element = target && (target[0] || target);
      if (!(element instanceof Element) || !element.closest('[data-hb-reviews]')) return fallback ? fallback(target, offset) : undefined;
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hb-header-height')) || 56;
      const top = Math.max(0, element.getBoundingClientRect().top + window.scrollY - header - 12);
      if (Math.abs(top - window.scrollY) > 1) window.scrollTo({ top, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      return false;
    };
  }

  function initWrite(section, tabs) {
    const widget = section.querySelector('[data-hb-reviews-panel="piece"] .jdgm-review-widget');
    if (!widget) return;
    whenJudgeme(tuneJudgeme);
    near(section, 600, () => whenJudgeme(requestForm));
    section.querySelectorAll('[data-hb-reviews-write]').forEach(button => button.addEventListener('click', () => {
      tabs.select('piece', true); // the button leaves with its panel: focus moves to the tab, then into Judge.me's form
      whenJudgeme(requestForm);
      const started = Date.now();
      const open = () => {
        const link = widget.querySelector('.jdgm-write-rev-link');
        const ready = link && typeof window.jdgm?.openForm === 'function' && link.style.display !== 'none';
        if (ready) {
          if (link.getAttribute('aria-expanded') !== 'true') link.click();
          return;
        }
        if (Date.now() - started < 10000) setTimeout(open, 120);
      };
      open();
    }));
  }

  // "This piece": each page Judge.me draws in the All reviews order (highest rated first, photos first within a
  // rating; its own order is the store's Judge.me setting and its sort select is hidden in CSS), and its
  // empty-state text in the words of the All reviews row. Idempotent: run at start (before the skeleton is fitted
  // to the rows), when Judge.me is set up and whenever it redraws the panel; rows only move when out of order, so
  // the redraw observer below settles.
  function tunePiece(section, strings) {
    const panel = section.querySelector('[data-hb-reviews-panel="piece"]');
    if (!panel || !panel.querySelector('.jdgm-widget')) return;
    const rank = node => (Number(node.querySelector('.jdgm-rev__rating')?.dataset.score) || 0) * 2 + (node.querySelector('.jdgm-rev__pic-link') ? 1 : 0);
    const apply = () => {
      panel.querySelectorAll('.jdgm-rev-widg__reviews').forEach(box => {
        const rows = [...box.children].filter(node => node.classList.contains('jdgm-rev'));
        const order = rows.map((node, index) => ({ node, index, rank: rank(node) })).sort((a, b) => b.rank - a.rank || a.index - b.index).map(item => item.node);
        if (order.every((node, index) => node === rows[index])) return;
        const anchor = document.createComment('');
        rows[0].before(anchor);
        order.forEach(node => anchor.before(node));
        anchor.remove();
      });
      if (strings.firstTitle) panel.querySelectorAll('.hb-reviews__app--first .jdgm-rev-widg__summary-text').forEach(text => {
        if (text.querySelector('.hb-reviews__first-title')) return;
        const title = document.createElement('strong');
        title.className = 'hb-reviews__first-title';
        title.textContent = strings.firstTitle;
        const note = document.createElement('span');
        note.className = 'hb-reviews__first-note';
        note.textContent = strings.firstNote || '';
        text.replaceChildren(title, ' ', note);
      });
    };
    apply();
    whenJudgeme(apply);
    if ('MutationObserver' in window) {
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; apply(); });
      }).observe(panel, { childList: true, subtree: true });
    }
  }

  // "This piece" skeleton (snippets/humbler-reviews-skeleton, 'widget'): each row is fitted to the review Judge.me
  // will draw in its place (its rows are already in the page, hidden): title or not, body lines at the row's width,
  // photo or not. Refitted on resize while the skeleton is up.
  function fitSkeleton(section) {
    const app = section.querySelector('.hb-reviews__app[data-hb-app-strict]');
    const skeleton = app?.querySelector(':scope > [data-hb-app-skeleton]');
    const rows = skeleton ? [...skeleton.querySelectorAll('[data-hb-skeleton-row]')] : [];
    if (!rows.length) return;
    const reviews = [...app.querySelectorAll('.jdgm-rev')].filter(node => !skeleton.contains(node)).slice(0, rows.length).map(node => ({
      title: !!clean(node.querySelector('.jdgm-rev__title')?.textContent),
      chars: clean(node.querySelector('.jdgm-rev__body')?.textContent).length,
      photo: !!node.querySelector('.jdgm-rev__pic-link')
    }));
    if (!reviews.length) return;
    const wide = window.matchMedia('(min-width: 1000px)');
    const fit = () => rows.forEach((row, index) => {
      const review = reviews[index];
      row.hidden = !review;
      if (!review) return;
      const text = row.querySelector('.hb-jdgm-skeleton__text');
      // DM Sans averages about 0.47em a character: 15px text (16px from 1000px) in the row's text column
      const perLine = Math.max(16, (text?.clientWidth || 320) / ((wide.matches ? 16 : 15) * 0.47));
      row.style.setProperty('--lines', String(Math.min(16, Math.max(1, Math.ceil(review.chars / perLine)))));
      const title = row.querySelector('.hb-jdgm-skeleton__title');
      const photo = row.querySelector('.hb-jdgm-skeleton__photo');
      if (title) title.hidden = !review.title;
      if (photo) photo.hidden = !review.photo;
    });
    fit();
    if (!('ResizeObserver' in window)) return;
    let width = skeleton.clientWidth;
    const observer = new ResizeObserver(() => {
      if (!skeleton.isConnected) { observer.disconnect(); return; }
      if (skeleton.clientWidth === width) return;
      width = skeleton.clientWidth;
      fit();
    });
    observer.observe(skeleton);
  }

  function initCarousel(section) {
    if (!section.querySelector('.jdgm-carousel-wrapper, .jdgm-carousel')) return;
    // A card's "read review" link (a card-sized overlay, humbler-reviews.css) that takes keyboard focus while its
    // card is outside the visible row slides the carousel there, with Judge.me's own slider (jdgm.performSliding).
    section.addEventListener('focusin', event => {
      const link = event.target.closest?.('.jdgm-carousel-item__full-review-link');
      const carousel = link?.closest('.jdgm-carousel');
      const box = carousel?.querySelector('.jdgm-carousel__item-container');
      const jdgm = window.jdgm;
      if (!box || typeof jdgm?.performSliding !== 'function' || !jdgm.$) return;
      try { if (!link.matches(':focus-visible')) return; } catch { /* no :focus-visible: slide for any focus */ }
      const item = link.closest('.jdgm-carousel-item');
      const items = [...carousel.querySelectorAll('.jdgm-carousel-item')];
      const view = box.getBoundingClientRect();
      const rect = item.getBoundingClientRect();
      if (rect.left >= view.left - 1 && rect.right <= view.right + 1) return;
      const $carousel = jdgm.$(carousel);
      const current = Number($carousel.data('current-slide')) || 0;
      const size = rect.width || 1;
      const perView = Math.max(1, Math.floor((view.width + 1) / size));
      const index = items.indexOf(item);
      const delta = index < current ? index - current : index - (current + perView - 1);
      try { jdgm.performSliding($carousel, delta, true); } catch (error) { console.warn('[humbler-reviews] carousel', error); }
    });
    near(section, 600, () => {
      const unveil = () => section.querySelectorAll('.jdgm-carousel img[data-src]:not([src])').forEach(img => {
        img.src = img.getAttribute('data-src');
        img.parentElement?.classList.remove('jdgm--loading');
      });
      unveil();
      // The carousel may still be drawing its items: catch any that arrive later.
      if ('MutationObserver' in window) {
        const observer = new MutationObserver(unveil);
        observer.observe(section, { childList: true, subtree: true });
        setTimeout(() => observer.disconnect(), 15000);
      }
    });
  }

  /* ---------- init ---------- */
  function init(section) {
    if (section.hbReviews) return;
    section.hbReviews = true;
    let strings = {};
    try { strings = JSON.parse(section.querySelector('[data-hb-reviews-i18n]')?.textContent || '{}'); } catch { strings = {}; }
    tunePiece(section, strings); // first: puts Judge.me's (still hidden) rows in order for the skeleton fit
    fitSkeleton(section);
    const tabs = initTabs(section);
    section.querySelectorAll('[data-hb-reviews-feed]').forEach(feed => initFeed(section, feed, tabs, strings));
    initLightbox(section);
    initWrite(section, tabs);
    // "Read all reviews" in the This piece fallback (Judge.me did not draw): switch tabs, focus the new tab.
    section.querySelectorAll('[data-hb-reviews-show]').forEach(button => button.addEventListener('click', () => tabs.select(button.dataset.hbReviewsShow, true)));
    initCarousel(section);
  }
  const scan = (scope = document) => scope.querySelectorAll('[data-hb-reviews]').forEach(section => {
    try { init(section); } catch (error) { console.error('[humbler-reviews]', error); }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));

  window.HumblerReviews = Object.freeze({ scan, parse });
})();
